import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { get } from 'idb-keyval';
import { useLambda } from '@/views/habit/awsLambda'
import { oid, idToString } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useProcessList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, updateItems, createItem, getItemsAggregate } = useLambda()
  const { processesQueryWithOffset } = useMongoDBFunctions()
  const { client = null, role = null, worker_id = null, locations = [] } = store.state?.userStore?.userData
  const processes = ref([])
  const isOnline = computed(() => store.state.app.isOnline)
  const collection = 'process'
  const locationFilter = ref([])
  const physicalLocationFilter = ref([])
  const isDuplicating = ref(false)

  // Table Headers
  const tableColumns = computed(() => {
    const headers = [
      { key: 'code', label: i18n.t('message.tableHeader.code'), sortable: true },
      { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
      { key: 'physicalLocations', label: i18n.t('message.tableHeader.physical_locations'), sortable: true},
      { key: 'locations', label: i18n.t('message.tableHeader.locations'), sortable: true},
      { key: 'activities', label: i18n.t('message.tableHeader.activities') },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]

    // Add require image and time capture columns for client Explora
    // TODO: Change this hardcoded client ID logic to a more generic one
    if (client.$oid === '688bc1183c0eff975f0aecb6') {
      headers.splice(4, 0, { key: 'requireImage', label: i18n.t('message.tableHeader.requireImage') }, { key: 'requireTimeCapture', label: i18n.t('message.tableHeader.requireTimeCapture') })
    }

    return headers
  })
  const totalProcesses = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'code'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchProcesses = async () => {
    isLoading.value = true

    if (isOnline.value) {
      try {
        const input = {
          limit: perPage.value,
          total: currentPage.value === 1,
          offset: (currentPage.value * perPage.value) - perPage.value,
          query: {},
          search: searchQuery.value,
          sortBy: sortBy.value || defaultSortBy,
          sortOrder: isSortDirDesc.value ? -1 : 1,
          client_id: (role !== 'admin') ? client.$oid : null,
          worker_id: (role !== 'admin') ? worker_id.$oid : null,
          role: role,
          locations: locations?.map(e => e.value),
          locationFilter: locationFilter.value,
          physicalLocationFilter: physicalLocationFilter.value,
        }
      
        const items = await processesQueryWithOffset(input)

        // Format locations and physical locations
        items.processes?.forEach(process => {
          const physicalLocations = new Set()
          process.locationsData?.forEach(l => {
            if (l.physicalLocationName) physicalLocations.add(l.physicalLocationName)
          })
          process.physicalLocations = Array.from(physicalLocations).join(', ')
          process.locations = process.locationsData?.map(l => l.location).join(', ') || ''
        })

        processes.value = items?.processes || []
        if (currentPage.value === 1) totalProcesses.value = items?.totalRecords || 0
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.process_list_fetch_error') })
      } finally {
        isLoading.value = false
      }
    }
    else {
      get('processesComplete').then(response => {
        if (response) processes.value = response
        else if (localStorage.processesComplete) processes.value = JSON.parse(localStorage.processesComplete)
        else handleError({ isStorageError: true })
        isLoading.value = false
      })
    }
  }

  const deleteProcess = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      showSuccessMessage(i18n.t('message.process_remove'))
      fetchProcesses()

      // Delete confirmation plannings associated to this process
      await updateItems({ collection: 'confirmation_planning', query: { process: oid(id) }, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.process_remove_error'))
    }
  }

  const duplicateProcess = async (id) => {
    isDuplicating.value = true
    try {
      const pipeline = [
        { $match: { _id: oid(id) } },
        { $lookup: { from: 'metadata', localField: 'metadata', foreignField: '_id', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ], as: 'metadataData' } },
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      const original = items?.[0]
      if (!original) throw new Error('Process not found')

      const payload = {
        client_id: oid(idToString(original.client_id)) || oid(client?.$oid),
        name: i18n.t('message.copy_of_process', { name: original.name }),
        code: original.code,
        goal: original.goal,
        description: original.description,
        requireImage: !!original.requireImage,
        requireTimeCapture: !!original.requireTimeCapture,
        metadata: original.metadataData?.map(m => oid(m._id)) || [],
        activities: JSON.parse(JSON.stringify(original.activities || [])),
      }

      await createItem({ collection, payload })

      showSuccessMessage(i18n.t('message.process_duplicated'))
      fetchProcesses()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.process_duplicate_error'))
    } finally {
      isDuplicating.value = false
    }
  }

  // TODO::Nipun - Make global mixin and import it or extend all composable with that
  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (processes.value.length ? 1 : 0)
    const to = processes.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalProcesses.value })
  })

  const updateFilter = (data) => {
    locationFilter.value = data.locationFilter
    physicalLocationFilter.value = data.physicalLocationFilter
    fetchProcesses()
  }

  watch([currentPage, perPage], () => {
    fetchProcesses()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchProcesses()
  })

  return {
    fetchProcesses,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalProcesses,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    processes,
    deleteProcess,
    duplicateProcess,
    isDuplicating,
    isLoading,
    updateFilter,
  }
}
